/**
 * Money Impact Calculation Module
 * 
 * Generates cumulative cost trajectories and savings data for comparison
 * between baseline "Do Nothing" and replacement options.
 * 
 * Includes:
 * - Cumulative cost simulation with replacements
 * - Savings vs baseline calculations
 * - Payback period calculations
 */

import type { SystemRecOutput, RecommendationOption, MoneyImpact } from './types';
import type { BaselineResult } from './baseline';

// ==================== TYPES ====================

export interface CostSimulatorParams {
  horizonYears: number;                // Analysis period (typically 20)
  annualRunningCostYear1: number;      // Annual running cost in year 1
  runningCostDegradationRate?: number; // Annual degradation rate (0.0-1.0)
  lifespanYears: number;               // System lifespan
  replacementCost: number;             // Cost per replacement
  upfrontCost?: number;                // Initial upfront cost (applied at year 0)
}

// ==================== COST SIMULATOR ====================

/**
 * Simulate cumulative cost over time with degradation and replacements
 * 
 * @param params - Cost simulation parameters
 * @returns Array of cumulative costs for each year (0..horizonYears)
 */
export function simulateCumulativeCost(params: CostSimulatorParams): number[] {
  const {
    horizonYears,
    annualRunningCostYear1,
    runningCostDegradationRate = 0,
    lifespanYears,
    replacementCost,
    upfrontCost = 0,
  } = params;

  const cumulativeCost: number[] = [];
  
  // Year 0: upfront cost
  cumulativeCost.push(Math.round(upfrontCost));
  
  let cumulative = upfrontCost;
  let systemAge = 0; // Track age since last replacement
  
  for (let year = 1; year <= horizonYears; year++) {
    systemAge++;
    
    // Calculate running cost for this year with degradation
    const degradationFactor = Math.pow(1 + runningCostDegradationRate, year - 1);
    const annualCost = annualRunningCostYear1 * degradationFactor;
    cumulative += annualCost;
    
    // Check if replacement is needed this year
    if (systemAge >= lifespanYears && replacementCost > 0) {
      cumulative += replacementCost;
      systemAge = 0; // Reset age after replacement
    }
    
    cumulativeCost.push(Math.round(cumulative));
  }
  
  return cumulativeCost;
}

// ==================== MONEY IMPACT GENERATOR ====================

/**
 * Generate MoneyImpact data from system recommendation output
 * 
 * @param output - System recommendation output
 * @param horizonYears - Analysis period (defaults to 20)
 * @returns MoneyImpact object with baseline and options cumulative costs/savings
 */
export function generateMoneyImpact(
  output: SystemRecOutput,
  horizonYears: number = 20
): MoneyImpact | null {
  const baseline = output.baselineResult as BaselineResult | undefined;
  const baselineSmartControl = output.baselineSmartControl ?? false;
  const inputEcho = output.inputEcho;
  
  if (!baseline) {
    return null; // Cannot generate without baseline
  }
  
  // ==================== BASELINE CUMULATIVE COST ====================
  
  // Use baseline's yearly breakdown which already includes degradation
  const baselineCumulative: number[] = [];
  baselineCumulative.push(0); // Year 0: no upfront cost for do-nothing
  
  let baselineCum = 0;
  for (let year = 1; year <= horizonYears; year++) {
    const yearData = baseline.yearlyBreakdown[year - 1];
    if (yearData) {
      baselineCum += yearData.totalAnnualCost_gbp;
    } else {
      // If we don't have yearly data, use annualized cost
      baselineCum += baseline.annualizedCost_gbp;
    }
    baselineCumulative.push(Math.round(baselineCum));
  }
  
  const baselineAnnualCostNow = baseline.yearlyBreakdown[0]?.totalAnnualCost_gbp || baseline.annualizedCost_gbp;
  const baselineTotalCost = baselineCumulative[horizonYears];
  
  // ==================== OPTIONS CUMULATIVE COST ====================
  
  const options: MoneyImpact['options'] = [];
  const allOptions = [output.primary, ...output.alternatives];
  
  for (const option of allOptions) {
    if (!option.lcoo) {
      continue; // Skip options without LCOO data
    }
    
    const lcoo = option.lcoo;
    
    // Get parameters for cost simulation
    const upfrontCost = lcoo.upfrontNetGBP || 0;
    let annualRunning = lcoo.annualRunningGBP || 0;
    const annualMaintenance = lcoo.annualMaintenanceGBP || 0;
    
    // Apply smart control savings to annual running cost
    // Smart control reduces fuel consumption, not maintenance
    if (option.includesSmartControl && !baselineSmartControl) {
      // Option has smart control but baseline doesn't - apply fuel savings
      const fuelType = option.boiler === 'air_source_hp' || option.boiler === 'ground_source_hp' 
        ? 'electricity' 
        : 'gas';
      
      if (fuelType === 'gas') {
        // Determine smart control type
        const smartControlType = inputEcho?.thermostatPreference?.preferredControlType;
        const savingsPct = smartControlType === 'smart_load_comp' ? 18 : 10;
        
        // Reduce running cost by savings percentage
        annualRunning = annualRunning * (1 - savingsPct / 100);
      }
    }
    
    const annualTotal = annualRunning + annualMaintenance;
    
    const lifespanYears = lcoo.systemLifespanYears || 15;
    const replacementCount = lcoo.replacementCount || 0;
    const replacementCost = (replacementCount > 0 && lcoo.replacementCostTotalGBP)
      ? lcoo.replacementCostTotalGBP / replacementCount
      : upfrontCost; // Default to upfront cost if no replacement data
    
    // Simulate cumulative cost with replacements
    const optionCumulative = simulateCumulativeCost({
      horizonYears,
      annualRunningCostYear1: annualTotal,
      runningCostDegradationRate: 0, // Options typically don't have degradation modeled
      lifespanYears,
      replacementCost,
      upfrontCost,
    });
    
    const optionTotalCost = optionCumulative[horizonYears];
    
    // Calculate savings vs baseline (per year)
    const savingsVsBaseline: number[] = [];
    for (let year = 0; year <= horizonYears; year++) {
      const saving = baselineCumulative[year] - optionCumulative[year];
      savingsVsBaseline.push(Math.round(saving));
    }
    
    const totalSavings = savingsVsBaseline[horizonYears];
    
    // Calculate simple payback if there are savings
    const annualSavingNow = baselineAnnualCostNow - annualTotal;
    let simplePaybackYears: number | undefined = undefined;
    
    if (annualSavingNow > 0 && upfrontCost > 0) {
      simplePaybackYears = Math.round((upfrontCost / annualSavingNow) * 10) / 10; // Round to 1 decimal
    }
    
    options.push({
      optionId: option.id,
      label: option.title,
      installNetCost: upfrontCost,
      annualRunningCostNow: annualTotal,
      cumulativeCost: optionCumulative,
      totalCost: optionTotalCost,
      cumulativeSavingsVsBaseline: savingsVsBaseline,
      totalSavingsVsBaseline: totalSavings,
      simplePaybackYears,
    });
  }
  
  return {
    horizonYears,
    baseline: {
      annualCostNow: Math.round(baselineAnnualCostNow),
      cumulativeCost: baselineCumulative,
      totalCost: baselineTotalCost,
    },
    options,
  };
}

// ==================== CHART DATA HELPERS ====================

/**
 * Extract chart-ready data for cumulative cost visualization
 */
export function getCumulativeCostChartData(moneyImpact: MoneyImpact) {
  const series = [
    {
      id: 'baseline',
      label: 'Do Nothing (Current System)',
      data: moneyImpact.baseline.cumulativeCost,
      isBaseline: true,
    },
    ...moneyImpact.options.map(opt => ({
      id: opt.optionId,
      label: opt.label,
      data: opt.cumulativeCost,
      isBaseline: false,
    })),
  ];
  
  return {
    series,
    horizonYears: moneyImpact.horizonYears,
  };
}

/**
 * Extract chart-ready data for cumulative savings visualization
 */
export function getCumulativeSavingsChartData(moneyImpact: MoneyImpact) {
  const series = moneyImpact.options.map(opt => ({
    id: opt.optionId,
    label: opt.label,
    data: opt.cumulativeSavingsVsBaseline,
  }));
  
  return {
    series,
    horizonYears: moneyImpact.horizonYears,
  };
}

/**
 * Get savings summary for the recommended option
 */
export function getRecommendedOptionSavings(moneyImpact: MoneyImpact) {
  // Primary option is first in the array
  const primary = moneyImpact.options[0];
  
  if (!primary) {
    return null;
  }
  
  const annualSavingNow = moneyImpact.baseline.annualCostNow - primary.annualRunningCostNow;
  const totalSavings = primary.totalSavingsVsBaseline;
  
  return {
    annualSaving: Math.round(annualSavingNow),
    totalSaving: totalSavings,
    paybackYears: primary.simplePaybackYears,
    horizonYears: moneyImpact.horizonYears,
  };
}
